import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Copy, Loader2, Plus, Power, Ticket, Trash2 } from 'lucide-react';
import api, { errMsg } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

const copyText = async (text) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      ta.remove();
    }
    toast.success('Kod kopyalandı');
  } catch (e) {
    toast.info(`Kodu elle kopyala: ${text}`);
  }
};

export const AdminCoupons = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [credits, setCredits] = useState(20);
  const [count, setCount] = useState(1);
  const [maxUses, setMaxUses] = useState(1);
  const [days, setDays] = useState('');
  const [customCode, setCustomCode] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [note, setNote] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/coupons');
      setItems(data.coupons);
    } catch (e) {
      toast.error(errMsg(e, 'Kodlar yüklenemedi'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    if (Number(credits) <= 0) {
      toast.error('Hak miktarı 0’dan büyük olmalı');
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post('/admin/coupons', {
        credits: Number(credits),
        count: Number(count),
        max_uses: Number(maxUses),
        note,
        code: customCode.trim() || null,
        expires_at: expiresAt || null,
        expires_in_days: !expiresAt && days ? Number(days) : null,
      });
      toast.success(`${data.coupons.length} kod üretildi`);
      if (data.coupons.length === 1) {
        copyText(data.coupons[0].code);
      }
      setNote('');
      setCustomCode('');
      await load();
    } catch (e) {
      toast.error(errMsg(e, 'Kod üretilemedi'));
    } finally {
      setCreating(false);
    }
  };

  const toggle = async (c) => {
    try {
      const { data } = await api.post(`/admin/coupons/${c.id}/toggle`);
      setItems((p) => p.map((x) => (x.id === c.id ? data.coupon : x)));
    } catch (e) {
      toast.error(errMsg(e, 'Güncellenemedi'));
    }
  };

  const remove = async (c) => {
    try {
      await api.delete(`/admin/coupons/${c.id}`);
      setItems((p) => p.filter((x) => x.id !== c.id));
      toast.success('Kod silindi');
    } catch (e) {
      toast.error(errMsg(e, 'Silinemedi'));
    }
  };

  return (
    <div className="space-y-5">
      <div className="panel p-5">
        <div className="flex items-center gap-2">
          <Ticket className="h-4 w-4 text-[hsl(var(--brand-red))]" />
          <h2 className="font-display text-sm font-semibold">Yeni Kapak Hakkı Kodu Üret</h2>
        </div>
        <p className="mt-1.5 text-xs text-[hsl(var(--text-muted))]">
          Ödemesini alıp kullanıcıya kod verirsen hakkı kendisi anında yükler; elle uğraşmazsın.
        </p>

        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Özel kod (opsiyonel)</Label>
            <Input
              data-testid="coupon-custom-code-input"
              value={customCode}
              onChange={(e) => setCustomCode(e.target.value.toUpperCase())}
              placeholder="Örn: SPUNKRAM"
              className="bg-[hsl(var(--surface-2))] font-mono-alt uppercase"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Hak</Label>
            <Input
              type="number"
              data-testid="coupon-credits-input"
              value={credits}
              onChange={(e) => setCredits(e.target.value)}
              className="bg-[hsl(var(--surface-2))] font-mono-alt"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Kaç kod</Label>
            <Input
              type="number"
              data-testid="coupon-count-input"
              value={count}
              onChange={(e) => setCount(e.target.value)}
              disabled={!!customCode.trim()}
              className="bg-[hsl(var(--surface-2))] font-mono-alt disabled:opacity-50"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Kullanım limiti</Label>
            <Input
              type="number"
              data-testid="coupon-maxuses-input"
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
              className="bg-[hsl(var(--surface-2))] font-mono-alt"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Son kullanma tarihi</Label>
            <Input
              type="date"
              data-testid="coupon-expires-at-input"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="bg-[hsl(var(--surface-2))] font-mono-alt"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">veya gün sonra bitsin</Label>
            <Input
              type="number"
              data-testid="coupon-days-input"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              disabled={!!expiresAt}
              placeholder="Sınırsız"
              className="bg-[hsl(var(--surface-2))] font-mono-alt disabled:opacity-50"
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs">Not</Label>
            <Input
              data-testid="coupon-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Örn: Ücretsiz kampanya kodu"
              className="bg-[hsl(var(--surface-2))]"
            />
          </div>
        </div>

        <p className="mt-3 text-xs text-[hsl(var(--text-muted))]">
          Özel kod girersen tek kod üretilir. Her hesap bir kodu yalnızca 1 kez kullanabilir;
          &quot;Kullanım limiti&quot; kaç farklı kullanıcının kodu kullanabileceğini belirler.
        </p>

        <Button
          onClick={create}
          disabled={creating}
          data-testid="coupon-create-button"
          className="mt-4 h-10 bg-[hsl(var(--brand-red))] font-semibold text-white hover:bg-[hsl(var(--brand-red-2))]"
        >
          {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
          Kod Üret
        </Button>
      </div>

      {loading ? (
        <div className="grid place-items-center py-14">
          <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--brand-red))]" />
        </div>
      ) : items.length === 0 ? (
        <p data-testid="empty-state" className="panel px-5 py-10 text-center text-sm text-[hsl(var(--text-muted))]">
          Henüz kapak hakkı kodu üretmedin.
        </p>
      ) : (
        <div data-testid="admin-coupons-list" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((c) => {
            const spent = c.used_count >= c.max_uses;
            return (
              <div key={c.id} data-testid={`admin-coupon-${c.id}`} className="panel p-4">
                <div className="flex items-center justify-between gap-2">
                  <code className="font-mono-alt text-sm font-bold tracking-wide text-[hsl(var(--brand-red))]">
                    {c.code}
                  </code>
                  <Button
                    size="icon"
                    variant="ghost"
                    data-testid={`coupon-copy-${c.id}`}
                    onClick={() => copyText(c.code)}
                    className="h-7 w-7 text-[hsl(var(--text-muted))] hover:bg-[hsl(var(--surface-2))] hover:text-white"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>

                <p className="mt-2 text-xs">
                  <span className="font-semibold">{c.credits} hak</span>
                  <span className="text-[hsl(var(--text-muted))]">
                    {' '}
                    - {c.used_count}/{c.max_uses} kullanıldı
                  </span>
                </p>
                {c.note && <p className="mt-1 truncate text-xs text-[hsl(var(--text-muted))]">{c.note}</p>}
                <p className="mt-1 font-mono-alt text-[10px] text-[hsl(var(--text-muted))]">
                  {c.expires_at
                    ? `Bitiş: ${new Date(c.expires_at).toLocaleDateString('tr-TR')}`
                    : 'Süresiz'}
                </p>
                {c.uses?.length > 0 && (
                  <p className="mt-1 truncate text-[10px] text-[hsl(var(--text-muted))]">
                    Kullanan: {c.uses.map((x) => x.user_email).join(', ')}
                  </p>
                )}

                <div className="mt-3 flex items-center gap-2">
                  <span
                    className={[
                      'rounded-full px-2 py-0.5 text-[10px] font-bold',
                      !c.active
                        ? 'bg-[hsl(var(--surface-2))] text-[hsl(var(--text-muted))]'
                        : spent
                        ? 'bg-[hsl(38,92%,50%)] text-black'
                        : 'bg-[hsl(142,70%,45%)] text-black',
                    ].join(' ')}
                  >
                    {!c.active ? 'Kapalı' : spent ? 'Tükendi' : 'Aktif'}
                  </span>
                  <Button
                    size="sm"
                    variant="secondary"
                    data-testid={`coupon-toggle-${c.id}`}
                    onClick={() => toggle(c)}
                    className="ml-auto h-8 border border-[hsl(var(--border-soft))] bg-[hsl(var(--surface-2))] text-xs"
                  >
                    <Power className="mr-1.5 h-3.5 w-3.5" /> {c.active ? 'Kapat' : 'Aç'}
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        size="icon"
                        variant="ghost"
                        data-testid={`coupon-delete-${c.id}`}
                        className="h-8 w-8 border border-[hsl(var(--border-soft))] text-[hsl(var(--text-muted))] hover:text-[hsl(var(--brand-red))]"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="border-[hsl(var(--border-soft))] bg-[hsl(var(--surface))]">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Kod silinsin mi?</AlertDialogTitle>
                        <AlertDialogDescription className="text-[hsl(var(--text-muted))]">
                          {c.code} kalıcı olarak silinecek.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="border-[hsl(var(--border-soft))] bg-[hsl(var(--surface-2))]">
                          Vazgeç
                        </AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => remove(c)}
                          data-testid={`coupon-delete-confirm-${c.id}`}
                          className="bg-[hsl(var(--brand-red))] text-white hover:bg-[hsl(var(--brand-red-2))]"
                        >
                          Sil
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
